/* eslint-disable react/prop-types */
import {
  type FC,
  useMemo,
  useState,
  forwardRef,
  useRef,
  useImperativeHandle,
  type MutableRefObject,
  useEffect,
} from 'react';
import classNames from 'classnames/bind';
import type { IconName } from '../Icons/Icons';
import { FAIcon } from '../Icons/Icons';
import { EVENT_KEY, MLPVersion } from '../types/enums';
import { useTranslation } from '../Translation/TranslationProvider';
import { type QuantityStepperPropType } from './types/propTypes';
import { QuantityStepperType, QuantityStepperSize, QuantityStepperStatus, QuantityStepperWidth } from './types/enums';
import styles from './QuantityStepper.module.scss';

const cx = classNames.bind(styles);

const currentStatusIcons = (status: QuantityStepperStatus | undefined, message: string): IconName | null => {
  if (message) {
    if (status === QuantityStepperStatus.VALID) {
      return 'circle-check';
    } else if (status === QuantityStepperStatus.INVALID) {
      return 'square-exclamation';
    }
  }
  return null;
};
const getProcessValue = (value: string, maxCount: number, prefix: string | undefined, minCount: number): string => {
  let currentVal = value;
  if (prefix) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const [_, val] = currentVal.split(prefix);
    currentVal = Number.isNaN(val) ? '' : (val as string);
  }
  const parsedValue = parseInt(currentVal);
  if (parsedValue > maxCount) {
    return maxCount.toString();
  }
  if (parsedValue < minCount) {
    return minCount.toString();
  }
  return currentVal;
};
const getInputValue = (
  prefix: string | undefined,
  isFocused: boolean,
  quantity: number,
  minCount: number,
  mlpVersion: MLPVersion,
): string => {
  let value = quantity.toString();
  if (mlpVersion === MLPVersion.TWO && prefix) {
    if (Number.isNaN(quantity)) {
      value = isFocused ? prefix : '';
    } else {
      value = prefix + value;
    }
  } else {
    if (Number.isNaN(quantity)) {
      value = isFocused ? '' : minCount.toString();
    }
  }
  return value;
};
const QuantityStepper: FC<QuantityStepperPropType> = forwardRef<HTMLInputElement, QuantityStepperPropType>(
  (
    {
      id = 'quantity',
      name = 'quantity',
      prefix,
      count = 0,
      minCount = 0,
      maxCount = 999,
      type = QuantityStepperType.OUTLINED,
      size = QuantityStepperSize.SMALL,
      disabled = false,
      message = '',
      disableMinusButton = false,
      disablePlusButton = false,
      inputChangeHandler = () => 0,
      minusClickHandler = () => 0,
      plusClickHandler = () => 0,
      status = QuantityStepperStatus.DEFAULT,
      ariaLabelledMinus,
      ariaLabelledPlus,
      mlpVersion = MLPVersion.ONE,
      step = 1,
      width = QuantityStepperWidth.DEFAULT,
      height,
      compact = false,
      ...restProps
    },
    ref,
  ) => {
    const t = useTranslation();
    const [quantity, setQuantity] = useState(count);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef() as MutableRefObject<HTMLInputElement>;
    const statusIcon = useMemo(() => currentStatusIcons(status, message), [status, message]);

    const ariaLabelledMinusValue = ariaLabelledMinus || t('nextgen.QuantityStepperMinus.ariaLabel');
    const ariaLabelledPlusValue = ariaLabelledPlus || t('nextgen.QuantityStepperPlus.ariaLabel');

    useImperativeHandle(ref, () => inputRef.current);

    const onMinusEvent = () => {
      if (!disabled && !disableMinusButton && quantity > minCount) {
        setQuantity(quantity - step);
        minusClickHandler?.(quantity - step);
      }
    };
    const onPlusEvent = () => {
      if (!disabled && !disablePlusButton && quantity < maxCount) {
        setQuantity((quantity || minCount) + step);
        plusClickHandler?.(quantity + step);
      }
    };
    useEffect(() => {
      setQuantity(count);
    }, [count]);
    const CClassname = cx(
      `quantity-stepper-${mlpVersion}`,
      { 'focused-element': isFocused },
      ...(mlpVersion === MLPVersion.ONE ? [type, size, width] : ''),
      { disabled: disabled },
      [status],
      { compact: compact && mlpVersion === MLPVersion.ONE && width === QuantityStepperWidth.FULL_WIDTH },
    );
    const CMinusBtnClassname = cx('minus', { inactive: quantity === minCount || disabled || disableMinusButton });
    const CPlusBtnClassname = cx('plus', { inactive: quantity === maxCount || disabled || disablePlusButton });
    return (
      <>
        <div
          className={CClassname}
          data-testid="quantity-stepper"
          style={height ? { height: `${height}px` } : undefined}
        >
          <div
            role="button"
            aria-label={ariaLabelledMinusValue}
            className={CMinusBtnClassname}
            tabIndex={0}
            data-testid="minus"
            onClick={onMinusEvent}
            onKeyDown={(e) => {
              if (e.key === EVENT_KEY.ENTER) {
                onMinusEvent();
              }
            }}
          >
            <FAIcon iconName={'minus'} className={styles['icon']} />
          </div>
          <button
            className={styles['quantity']}
            onClick={() => inputRef?.current?.focus()}
            data-testid="quantity-wrapper"
            tabIndex={-1}
          >
            <input
              type="text"
              data-testid="quantity-value"
              ref={inputRef}
              name={name}
              min={minCount}
              max={maxCount}
              id={id}
              disabled={disabled}
              value={getInputValue(prefix, isFocused, quantity, minCount, mlpVersion)}
              inputMode="numeric"
              {...restProps}
              onFocus={(e) => {
                restProps?.onFocus?.(e);
                setIsFocused(true);
              }}
              onBlur={(e) => {
                restProps?.onBlur?.(e);
                setIsFocused(false);
              }}
              onChange={(e) => {
                if (!disabled) {
                  const value = getProcessValue(e.target.value, maxCount, prefix, minCount);
                  setQuantity(parseInt(value));
                  inputChangeHandler?.(parseInt(value) || minCount);
                }
              }}
              autoComplete="false"
            />
          </button>
          <div
            role="button"
            aria-label={ariaLabelledPlusValue}
            className={CPlusBtnClassname}
            tabIndex={0}
            data-testid="plus"
            onClick={onPlusEvent}
            onKeyDown={(e) => {
              if (e.key === EVENT_KEY.ENTER) {
                onPlusEvent();
              }
            }}
          >
            <FAIcon iconName={'plus'} className={styles['icon']} />
          </div>
        </div>
        <div className={styles['quantity-stepper-message']}>
          {statusIcon && <FAIcon iconName={statusIcon} />}
          <span>{message}</span>
        </div>
      </>
    );
  },
);
QuantityStepper.displayName = 'QuantityStepper';
export default QuantityStepper;
export { QuantityStepperType, QuantityStepperSize, QuantityStepperStatus, QuantityStepperWidth };
